/**
 * Tasks — marking a task complete, as the Tasks list's checkbox does it (Admin redesign Phase A1,
 * 2026-10-06; first used by src/components/Followups/AdminTaskRow.vue, "Your tasks" on /app/admin).
 *
 * Plan: C:\Users\nilay\.claude\plans\eager-painting-hejlsberg.md — "Phase A — A1".
 *
 * ⛔ KEEP THIS FILE IMPORT-FREE (the node-tests execute it verbatim). Everything that needs moment,
 *    the DOM or the store is computed by the caller and passed in.
 *
 * ── THE SOURCE OF TRUTH ────────────────────────────────────────────────────────────────────────
 * src/pages/TaskList/components/TodoItem.vue `changeStatus` (INCOMPLETE → COMPLETED branch) and
 * the handler every host gives its `change` event (AdminPage.vue / ToDo.vue `handleItemChange`):
 *   1. a REPEATING task whose due date is today or later (moment(dueAtStr).diff(moment(), 'days')
 *      >= 0 — an older one was already rolled over by the nightly reset) gets its next instance:
 *      next due = getters['tasks/getNextDueDate'](task); unless an INCOMPLETE task of the same
 *      sequence is already due that day (sameRepeatSequence below — same creator, frequency,
 *      weekday / day / month, and the same cleaned text), dispatch
 *        tasks/addPersonalTask  { task: <copy without id and subtasks, INCOMPLETE, new dates>, notification: [] }
 *   2. then dispatch
 *        tasks/updateTask  { ...task, dueAt, dueAtStr, status: 'COMPLETED', createdAt,
 *                            completedAt, completedAtStr, completedByEmpID }
 *      with handleItemChange's guard: a completedAt whose toISOString() throws is dropped
 *      (with its completedAtStr).
 * cypress/node-tests/tasks-complete-task.test.js reads those lines back out of TodoItem.vue and
 * AdminPage.vue and fails if they change — fix THIS file to match, never the other way round.
 *
 * Two kept-on-purpose details: the next instance is a spread of the task (every field it has,
 * `key` included) with `id` left out; and the duplicate check compares the CLEANED text, failing
 * safe (no match → create) if cleaning throws.
 */

/** TodoItem.combinedTasksFromStore: myCreated + assignedToMe + myEmployees, one per id, last wins. */
export function combineStoreTasks(created, assigned, employees) {
  const list = [].concat(
    Array.isArray(created) ? created : [],
    Array.isArray(assigned) ? assigned : [],
    Array.isArray(employees) ? employees : [],
  );
  const byId = new Map();
  list.forEach((task) => {
    if (task && task.id) byId.set(task.id, task);
  });
  return Array.from(byId.values());
}

/** TodoItem: `if (this.item.repeat && diff >= 0)` — diff = moment(dueAtStr).diff(moment(), 'days'). */
export function needsNextInstance(task, diffDays) {
  return !!(task && task.repeat) && diffDays >= 0;
}

function hasDay(t) {
  return t.repeatOnDay != null && t.repeatOnDay !== '';
}

function sameDayRule(item, existing) {
  const itemHasLastDay = item.repeatOnLastDayOfMonth === true;
  const existingHasLastDay = existing.repeatOnLastDayOfMonth === true;
  const itemHasDay = hasDay(item);
  const existingHasDay = hasDay(existing);
  if (itemHasLastDay !== existingHasLastDay) return false;
  if (itemHasDay && existingHasDay && String(existing.repeatOnDay) !== String(item.repeatOnDay)) return false;
  if (!itemHasLastDay && !existingHasLastDay && itemHasDay !== existingHasDay) return false;
  return true;
}

/**
 * TodoItem's duplicate check: is `existing` the next instance of `item`'s repeating sequence, due
 * on `nextDueAtStr` ('MM/DD/YYYY')? `textOf(content)` = the cleaned task text (cleanTaskContent).
 */
export function sameRepeatSequence(item, existing, nextDueAtStr, textOf) {
  if (!item || !existing) return false;
  if (existing.dueAtStr !== nextDueAtStr
    || existing.status !== 'INCOMPLETE'
    || existing.createdByEmpID !== item.createdByEmpID
    || existing.repeatFrequency !== item.repeatFrequency) {
    return false;
  }
  const freq = item.repeatFrequency;
  if (freq === 'Weekly' || freq === 'Bi-Weekly') {
    if (existing.repeatOn !== item.repeatOn) return false;
  } else if (freq === 'Monthly') {
    if (!sameDayRule(item, existing)) return false;
  } else if (freq === 'Yearly') {
    if (String(existing.repeatOnMonth) !== String(item.repeatOnMonth)) return false;
    if (!sameDayRule(item, existing)) return false;
  }
  try {
    if (textOf(item.content) !== textOf(existing.content)) return false;
  } catch (e) {
    return false;
  }
  return true;
}

/** The existing next instance among `tasks`, or null (also when `tasks` is not an array). */
export function findNextInstance(item, tasks, nextDueAtStr, textOf) {
  if (!Array.isArray(tasks)) return null;
  return tasks.find(t => sameRepeatSequence(item, t, nextDueAtStr, textOf)) || null;
}

/**
 * The tasks/addPersonalTask payload for the next instance: the task without its id and subtasks,
 * INCOMPLETE, with the new created / due dates and no completion fields.
 * @param {Object} dates { createdAt: Date (now), nextDueAt: Date, nextDueAtStr: 'MM/DD/YYYY' }
 */
export function nextInstancePayload(item, { createdAt, nextDueAt, nextDueAtStr } = {}) {
  const task = {};
  Object.keys(item || {}).forEach((k) => {
    if (k !== 'id') task[k] = item[k];
  });
  Object.assign(task, {
    createdAt,
    dueAt: nextDueAt,
    dueAtStr: nextDueAtStr,
    status: 'INCOMPLETE',
    completedAt: null,
    completedAtStr: null,
    completedByEmpID: null,
  });
  if (task.subtasks) delete task.subtasks;
  return { task, notification: [] };
}

/**
 * The tasks/updateTask payload that completes the task (TodoItem's changeObj, then
 * handleItemChange's completedAt guard). Falsy completion fields are left out, as the spreads do.
 * @param {Object} v { dueAt, dueAtStr, createdAt, completedAt, completedAtStr, completedByEmpID }
 */
export function completedTaskPayload(item, v = {}) {
  const change = Object.assign({}, item, {
    dueAt: v.dueAt,
    dueAtStr: v.dueAtStr,
    status: 'COMPLETED',
    createdAt: v.createdAt,
  });
  if (v.completedAt) change.completedAt = v.completedAt;
  if (v.completedAtStr) change.completedAtStr = v.completedAtStr;
  if (v.completedByEmpID) change.completedByEmpID = v.completedByEmpID;
  if (change.completedAt) {
    try {
      change.completedAt.toISOString();
    } catch (e) {
      delete change.completedAt;
      delete change.completedAtStr;
    }
  }
  return change;
}

/**
 * TodoItem.cleanContent: the task's HTML → { text, employees, patients, tags }. Badges are pulled
 * out of the text: `data-teamio-employeeid` → employees, `data-teamio-patientid` → patients, any
 * other `.badge` → tags. `doc` = a Document (window.document); without one, the text is the HTML
 * with its tags removed and no badges.
 */
export function cleanTaskContent(content, doc) {
  const result = { text: '', employees: [], patients: [], tags: [] };
  if (!doc || typeof doc.createElement !== 'function') {
    result.text = String(content == null ? '' : content).replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
    return result;
  }
  const tempDiv = doc.createElement('div');
  tempDiv.innerHTML = content;
  const processNode = (node) => {
    if (node.nodeType === 3) return node.textContent.trim();
    if (node.nodeType === 1) {
      if (node.classList.contains('badge')) {
        if (node.hasAttribute('data-teamio-employeeid')) {
          result.employees.push({ id: node.getAttribute('data-teamio-employeeid'), name: node.textContent.trim() });
          return '';
        }
        if (node.hasAttribute('data-teamio-patientid')) {
          result.patients.push({ id: node.getAttribute('data-teamio-patientid'), name: node.textContent.trim() });
          return '';
        }
        const tagText = node.textContent.trim();
        if (tagText) result.tags.push(tagText);
        return '';
      }
      return Array.from(node.childNodes).map(processNode).filter(t => t).join(' ');
    }
    return '';
  };
  result.text = processNode(tempDiv).replace(/\s+/g, ' ').trim();
  return result;
}
